import { FloatingFormItem, InputDecimal } from '@/components/_common';
import { Card, Row, Col, FormInstance, Form } from 'antd';
import { InvoicePartFormValues } from '../InvoicePartForm.types';
import { useInvoiceFormContext } from '../../../InvoiceFormProvider';
import { PisCofinsCst } from '@/core/domain/invoice/invoiceNoteItems/invoiceNoteItemsParts/InvoiceNoteItems.types';
import { useCallback } from 'react';
import { TaxePisCstSelect } from '@/components/_common/TaxesSelect';

interface Props {
  form: FormInstance<InvoicePartFormValues>;
}

const InvoicePartCofinsForm: React.FC<Props> = ({ form }) => {
  const { disabledFields } = useInvoiceFormContext();

  const cofinsCst = Form.useWatch(['itemTaxes', 'cofins', 'cofinsCst'], form);

  const hasCstMonophasic =
    cofinsCst === PisCofinsCst.TAXABLE_OPERATION_MONOPHASIC;

  const handleCst = useCallback(
    (cstValue: PisCofinsCst) => {
      if (cstValue === PisCofinsCst.TAXABLE_OPERATION_MONOPHASIC) {
        form.setFieldsValue({
          itemTaxes: {
            cofins: {
              cofinsAliquot: undefined,
              cofinsValue: undefined,
              cofinsBaseCalculationValue: undefined,
              cofinsSoldQuantity: undefined,
              cofinsRealAliquot: undefined,
              cofinsStAliquot: undefined,
              cofinsStValue: undefined,
              cofinsStBaseCalculation: undefined,
              cofinsStSoldQuantity: undefined,
              cofinsStRealAliquot: undefined,
            },
          },
        });
      }
    },
    [form],
  );

  return (
    <Form form={form} preserve={false} disabled={disabledFields}>
      <Card title="COFINS" type="inner" style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <FloatingFormItem
              label="CST"
              name={['itemTaxes', 'cofins', 'cofinsCst']}
              getValueFromEvent={(value) => {
                handleCst(value);
                return value;
              }}
            >
              <TaxePisCstSelect allowClear />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={12} md={8} lg={5}>
            <FloatingFormItem
              label="Alíquota"
              name={['itemTaxes', 'cofins', 'cofinsAliquot']}
            >
              <InputDecimal fractions={4} max={100} />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={12} md={8} lg={5}>
            <FloatingFormItem
              label="Valor"
              name={['itemTaxes', 'cofins', 'cofinsValue']}
            >
              <InputDecimal disabled={hasCstMonophasic} />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={12} md={8} lg={5}>
            <FloatingFormItem
              label="Base de cálculo"
              name={['itemTaxes', 'cofins', 'cofinsBaseCalculationValue']}
            >
              <InputDecimal disabled={hasCstMonophasic} />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={12} md={8} lg={5}>
            <FloatingFormItem
              label="Quantidade vendida"
              name={['itemTaxes', 'cofins', 'cofinsSoldQuantity']}
            >
              <InputDecimal disabled={hasCstMonophasic} />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={12} md={8} lg={4}>
            <FloatingFormItem
              label="Alíquota em reais"
              name={['itemTaxes', 'cofins', 'cofinsRealAliquot']}
            >
              <InputDecimal disabled={hasCstMonophasic} />
            </FloatingFormItem>
          </Col>
        </Row>
      </Card>

      <Card title="COFINS ST" type="inner">
        <Row gutter={[24, 16]}>
          <Col span={24} sm={12} md={8} lg={5}>
            <FloatingFormItem
              label="Alíquota"
              name={['itemTaxes', 'cofins', 'cofinsStAliquot']}
            >
              <InputDecimal
                fractions={4}
                max={100}
                disabled={hasCstMonophasic}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={12} md={8} lg={5}>
            <FloatingFormItem
              label="Valor"
              name={['itemTaxes', 'cofins', 'cofinsStValue']}
            >
              <InputDecimal disabled={hasCstMonophasic} />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={12} md={8} lg={5}>
            <FloatingFormItem
              label="Base de cálculo"
              name={['itemTaxes', 'cofins', 'cofinsStBaseCalculation']}
            >
              <InputDecimal disabled={hasCstMonophasic} />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={12} md={8} lg={5}>
            <FloatingFormItem
              label="Quantidade vendida"
              name={['itemTaxes', 'cofins', 'cofinsStSoldQuantity']}
            >
              <InputDecimal disabled={hasCstMonophasic} />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={12} md={8} lg={4}>
            <FloatingFormItem
              label="Alíquota em reais"
              name={['itemTaxes', 'cofins', 'cofinsStRealAliquot']}
            >
              <InputDecimal disabled={hasCstMonophasic} />
            </FloatingFormItem>
          </Col>
        </Row>
      </Card>
    </Form>
  );
};

export default InvoicePartCofinsForm;
